
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  image: string;
  category: string;
  categoryNL: string;
  availability: string;
  availabilityNL: string;
  rating: number;
  cpu: string;
  ram: string;
  graphics: string;
  storage?: string;
  display?: string;
  brand: string;
  type: string;
  cpuBrand: string;
  gpuBrand: string;
  ramCapacity: string;
  descriptionNL: string;
  priceLabelNL: string;
  priceLabelEN: string;
}

const WorkstationsPage = () => {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [compareProducts, setCompareProducts] = useState<number[]>([]);
  const [showComparisonBar, setShowComparisonBar] = useState(false);
  const [filters, setFilters] = useState({
    type: [] as string[],
    brand: [] as string[],
    cpu: [] as string[],
    gpu: [] as string[],
    ram: [] as string[],
    availability: [] as string[],
    priceMin: 0,
    priceMax: 23000,
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [perPage, setPerPage] = useState(24);

  const products: Product[] = [
    {
      id: 1,
      name: 'Dell Precision 7000 Tower',
      description:
        'High-performance tower workstation for CAD, engineering, and content creation with dual Intel Xeon processors.',
      descriptionNL:
        'Krachtige tower-werkstation voor CAD, engineering en contentcreatie met dubbele Intel Xeon-processors.',
      price: 6255,
      image:
        'https://public.readdy.ai/ai/img_res/5772fb26620ade02c376058f9f820b9b.jpg',
      category: 'Tower Workstation',
      categoryNL: 'Tower Werkstation',
      availability: 'In Stock',
      availabilityNL: 'Op Voorraad',
      rating: 4.5,
      cpu: 'Intel Xeon W-2295',
      ram: '64GB DDR4 ECC',
      graphics: 'NVIDIA RTX A4000',
      storage: '1TB NVMe SSD',
      brand: 'dell',
      type: 'tower',
      cpuBrand: 'intel',
      gpuBrand: 'nvidia',
      ramCapacity: '64gb',
      priceLabelNL: 'Neem contact op voor prijzen',
      priceLabelEN: 'Contact us for pricing',
    },
    {
      id: 2,
      name: 'HP Z8 G5 Workstation',
      description:
        'Flagship dual-processor workstation for the most demanding professional applications and AI workloads.',
      descriptionNL:
        'Vlaggenschip werkstation met dubbele processor voor de meest veeleisende professionele toepassingen en AI-workloads.',
      price: 13155,
      image:
        'https://readdy.ai/api/search-image?query=hp%20z8%20workstation%20tower%20computer%20with%20premium%20silver%20and%20black%20finish%20dual%20processor%20system%20on%20pure%20white%20studio%20background%20front%20angle%20view%20showcasing%20professional%20enterprise%20grade%20design%20with%20mesh%20ventilation%20and%20robust%20construction%20high%20end%20product%20photography&width=400&height=400&seq=ws002&orientation=squarish',
      category: 'Tower Workstation',
      categoryNL: 'Tower Werkstation',
      availability: 'In Stock',
      availabilityNL: 'Op Voorraad',
      rating: 5,
      cpu: 'Dual Intel Xeon W-3345',
      ram: '128GB DDR4 ECC',
      graphics: 'NVIDIA RTX A6000',
      storage: '2TB NVMe SSD',
      brand: 'hp',
      type: 'tower',
      cpuBrand: 'intel',
      gpuBrand: 'nvidia',
      ramCapacity: '128gb',
      priceLabelNL: 'Neem contact op voor prijzen',
      priceLabelEN: 'Contact us for pricing',
    },
    {
      id: 3,
      name: 'Lenovo ThinkStation P360 Ultra',
      description:
        'Ultra-compact workstation with professional-grade performance in a space-saving design.',
      descriptionNL:
        'Ultra-compact werkstation met professionele prestaties in een ruimtebesparend ontwerp.',
      price: 3587,
      image:
        'https://readdy.ai/api/search-image?query=lenovo%20thinkstation%20ultra%20compact%20small%20form%20factor%20workstation%20computer%20with%20matte%20black%20finish%20and%20red%20accents%20on%20clean%20white%20studio%20background%20showing%20sleek%20minimalist%20design%20with%20compact%20dimensions%20and%20professional%20appearance%20modern%20product%20photography&width=400&height=400&seq=ws003&orientation=squarish',
      category: 'Small Form Factor',
      categoryNL: 'Compact Formaat',
      availability: 'Pre-Order',
      availabilityNL: 'Voorbestelling',
      rating: 4,
      cpu: 'Intel Core i9-12900K',
      ram: '32GB DDR4',
      graphics: 'NVIDIA RTX A2000',
      storage: '512GB NVMe SSD',
      brand: 'lenovo',
      type: 'sff',
      cpuBrand: 'intel',
      gpuBrand: 'nvidia',
      ramCapacity: '16gb-32gb',
      priceLabelNL: 'Neem contact op voor prijzen',
      priceLabelEN: 'Contact us for pricing',
    },
    {
      id: 4,
      name: 'Dell Precision 5570',
      description:
        '15.6" mobile workstation with professional graphics and ISV certification for on-the-go productivity.',
      descriptionNL:
        '15,6" mobiel werkstation met professionele grafische kaart en ISV-certificering voor productiviteit onderweg.',
      price: 2667,
      image:
        'https://readdy.ai/api/search-image?query=dell%20precision%20mobile%20workstation%20laptop%20computer%20open%20at%20angle%20showing%20screen%20and%20keyboard%20with%20professional%20silver%20aluminum%20chassis%20on%20pure%20white%20studio%20background%20sleek%20thin%20design%20with%20premium%20build%20quality%20and%20modern%20professional%20appearance%20high%20quality%20product%20photo&width=400&height=400&seq=ws004&orientation=squarish',
      category: 'Mobile Workstation',
      categoryNL: 'Mobiel Werkstation',
      availability: 'In Stock',
      availabilityNL: 'Op Voorraad',
      rating: 4.5,
      cpu: 'Intel Core i7-12800H',
      ram: '32GB DDR5',
      graphics: 'NVIDIA RTX A1000',
      display: '15.6" 4K OLED',
      brand: 'dell',
      type: 'mobile',
      cpuBrand: 'intel',
      gpuBrand: 'nvidia',
      ramCapacity: '16gb-32gb',
      priceLabelNL: 'Neem contact op voor prijzen',
      priceLabelEN: 'Contact us for pricing',
    },
    {
      id: 5,
      name: 'Apple Mac Pro',
      description:
        'Professional workstation with Apple M2 Ultra chip, designed for video editing and creative workflows.',
      descriptionNL:
        'Professioneel werkstation met Apple M2 Ultra-chip, ontworpen voor videobewerking en creatieve workflows.',
      price: 6439,
      image:
        'https://readdy.ai/api/search-image?query=apple%20mac%20pro%20tower%20workstation%20computer%20with%20distinctive%20silver%20aluminum%20chassis%20and%20lattice%20pattern%20design%20on%20clean%20white%20studio%20background%20front%20view%20showing%20iconic%20professional%20design%20with%20handles%20and%20premium%20metal%20construction%20apple%20product%20photography%20style&width=400&height=400&seq=ws005&orientation=squarish',
      category: 'Tower Workstation',
      categoryNL: 'Tower Werkstation',
      availability: 'Custom Config',
      availabilityNL: 'Aangepaste Configuratie',
      rating: 5,
      cpu: 'Apple M2 Ultra',
      ram: '64GB Unified Memory',
      graphics: '76-core GPU',
      storage: '1TB SSD',
      brand: 'apple',
      type: 'tower',
      cpuBrand: 'apple',
      gpuBrand: 'integrated',
      ramCapacity: '64gb',
      priceLabelNL: 'Aangepaste configuratie',
      priceLabelEN: 'Custom configuration',
    },
    {
      id: 6,
      name: 'HP ZBook Studio G9',
      description:
        '16" mobile workstation with professional graphics and DreamColor display for creative professionals.',
      descriptionNL:
        '16" mobiel werkstation met professionele grafische kaart en DreamColor-scherm voor creatieve professionals.',
      price: 3863,
      image:
        'https://readdy.ai/api/search-image?query=hp%20zbook%20studio%20mobile%20workstation%20laptop%20computer%20open%20showing%20large%20display%20and%20keyboard%20with%20sleek%20dark%20gray%20aluminum%20body%20on%20pure%20white%20studio%20background%20premium%20thin%20and%20light%20design%20with%20professional%20appearance%20and%20high%20end%20build%20quality%20product%20photography&width=400&height=400&seq=ws006&orientation=squarish',
      category: 'Mobile Workstation',
      categoryNL: 'Mobiel Werkstation',
      availability: 'In Stock',
      availabilityNL: 'Op Voorraad',
      rating: 4,
      cpu: 'Intel Core i9-12900H',
      ram: '64GB DDR5',
      graphics: 'NVIDIA RTX A3000',
      display: '16" 4K DreamColor',
      brand: 'hp',
      type: 'mobile',
      cpuBrand: 'intel',
      gpuBrand: 'nvidia',
      ramCapacity: '64gb',
      priceLabelNL: 'Neem contact op voor prijzen',
      priceLabelEN: 'Contact us for pricing',
    },
  ];

  const handleCompareToggle = (productId: number) => {
    if (compareProducts.includes(productId)) {
      const newCompare = compareProducts.filter((id) => id !== productId);
      setCompareProducts(newCompare);
      if (newCompare.length === 0) {
        setShowComparisonBar(false);
      }
    } else {
      if (compareProducts.length < 4) {
        const newCompare = [...compareProducts, productId];
        setCompareProducts(newCompare);
        setShowComparisonBar(true);
      }
    }
  };

  const clearComparison = () => {
    setCompareProducts([]);
    setShowComparisonBar(false);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setFilters((prev) => {
      const currentFilter = prev[filterType as keyof typeof prev] as string[];
      if (currentFilter.includes(value)) {
        return {
          ...prev,
          [filterType]: currentFilter.filter((v) => v !== value),
        };
      } else {
        return {
          ...prev,
          [filterType]: [...currentFilter, value],
        };
      }
    });
  };

  const clearAllFilters = () => {
    setFilters({
      type: [],
      brand: [],
      cpu: [],
      gpu: [],
      ram: [],
      availability: [],
      priceMin: 0,
      priceMax: 23000,
    });
  };

  const getAvailabilityBadge = (availability: string) => {
    switch (availability) {
      case 'In Stock':
        return 'bg-green-100 text-green-800';
      case 'Pre-Order':
        return 'bg-yellow-100 text-yellow-800';
      case 'Custom Config':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Tower Workstation':
        return 'text-primary bg-blue-50';
      case 'Small Form Factor':
        return 'text-green-600 bg-green-50';
      case 'Mobile Workstation':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <div key={`full-${i}`} className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-fill"></i>
        </div>
      );
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-half-fill"></i>
        </div>
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <div key={`empty-${i}`} className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-line"></i>
        </div>
      );
    }

    return stars;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm">
            <a href={prefix || '/'} className="text-gray-600 hover:text-primary flex items-center">
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-home-line"></i>
              </div>
              Home
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="text-gray-600 hover:text-primary">
              Hardware
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-900 font-medium">{isNL ? 'Werkstations' : 'Workstations'}</span>
          </nav>
        </div>
      </div>

      {/* Header Section */}
      <div className="bg-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-start">
            <div className="max-w-4xl">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {isNL ? 'Professionele Werkstations' : 'Professional Workstations'}
              </h1>
              <p className="text-gray-600 mb-6">
                {isNL
                  ? 'Krachtige werkstations voor CAD, 3D-modellering, videobewerking, wetenschappelijk rekenen en professionele toepassingen.'
                  : 'High-performance workstations for CAD, 3D modeling, video editing, scientific computing, and professional applications.'}
              </p>

              {/* Brand and Graphics Partners */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isNL ? 'Toonaangevende Werkstationmerken' : 'Leading Workstation Brands'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-computer-line text-[#007DB8] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Dell Precision</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-computer-line text-[#0096D6] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">HP Z Series</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-computer-line text-[#E2231A] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Lenovo ThinkStation</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-apple-fill text-[#000000] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Apple Mac Pro</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isNL ? 'Grafische Partners' : 'Graphics Partners'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-cpu-line text-[#76B900] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">NVIDIA RTX</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-cpu-fill text-[#ED1C24] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">AMD Radeon Pro</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-cpu-line text-[#0071C5] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Intel Arc Pro</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-computer-line text-[#1BA0D7] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Quadro Series</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Categories and Applications */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isNL ? 'Werkstationcategorieën' : 'Workstation Categories'}
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'Tower Werkstations - Maximale Prestaties' : 'Tower Workstations - Maximum Performance'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'Compact Formaat - Ruimtebesparend' : 'Small Form Factor - Space Efficient'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'Mobiele Werkstations - Draagbare Kracht' : 'Mobile Workstations - Portable Power'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'All-in-One Werkstations' : 'All-in-One Workstations'}
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isNL ? 'Professionele Toepassingen' : 'Professional Applications'}
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'CAD &amp; Technisch Ontwerp' : 'CAD &amp; Engineering Design'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? '3D-Modellering &amp; Animatie' : '3D Modeling &amp; Animation'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'Videobewerking &amp; Postproductie' : 'Video Editing &amp; Post-Production'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'Wetenschappelijk Rekenen &amp; AI' : 'Scientific Computing &amp; AI'}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                className="flex items-center text-gray-600 hover:text-primary cursor-pointer"
                onClick={() => setShowComparisonBar(!showComparisonBar)}
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-scales-3-line"></i>
                </div>
                {isNL ? 'Vergelijken' : 'Compare'} (<span>{compareProducts.length}</span>)
              </button>

              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">{isNL ? 'Weergave:' : 'View:'}</span>
                <button
                  className={`p-2 rounded cursor-pointer ${
                    viewMode === 'grid' ? 'text-primary bg-blue-50' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  onClick={() => setViewMode('grid')}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-grid-line"></i>
                  </div>
                </button>
                <button
                  className={`p-2 rounded cursor-pointer ${
                    viewMode === 'list' ? 'text-primary bg-blue-50' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  onClick={() => setViewMode('list')}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-list-check"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-16">
        <div className="flex gap-8">
          {/* Filters Sidebar */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-[172px]">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">{isNL ? 'Filters' : 'Filters'}</h3>
                <button className="text-primary text-sm hover:underline cursor-pointer" onClick={clearAllFilters}>
                  {isNL ? 'Alles Wissen' : 'Clear All'}
                </button>
              </div>

              {/* Workstation Type */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Werkstationtype' : 'Workstation Type'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.type.includes('tower')}
                      onChange={() => handleFilterChange('type', 'tower')}
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      {isNL ? 'Tower Werkstations' : 'Tower Workstations'}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.type.includes('sff')}
                      onChange={() => handleFilterChange('type', 'sff')}
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      {isNL ? 'Compact Formaat' : 'Small Form Factor'}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.type.includes('mobile')}
                      onChange={() => handleFilterChange('type', 'mobile')}
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      {isNL ? 'Mobiele Werkstations' : 'Mobile Workstations'}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.type.includes('aio')}
                      onChange={() => handleFilterChange('type', 'aio')}
                    />
                    <span className="ml-3 text-sm text-gray-700">All-in-One</span>
                  </label>
                </div>
              </div>

              {/* Brand */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Merk' : 'Brand'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.brand.includes('dell')}
                      onChange={() => handleFilterChange('brand', 'dell')}
                    />
                    <span className="ml-3 text-sm text-gray-700">Dell Precision</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.brand.includes('hp')}
                      onChange={() => handleFilterChange('brand', 'hp')}
                    />
                    <span className="ml-3 text-sm text-gray-700">HP Z Series</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.brand.includes('lenovo')}
                      onChange={() => handleFilterChange('brand', 'lenovo')}
                    />
                    <span className="ml-3 text-sm text-gray-700">Lenovo ThinkStation</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.brand.includes('apple')}
                      onChange={() => handleFilterChange('brand', 'apple')}
                    />
                    <span className="ml-3 text-sm text-gray-700">Apple Mac Pro</span>
                  </label>
                </div>
              </div>

              {/* CPU Brand */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Processormerk' : 'CPU Brand'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.cpu.includes('intel')}
                      onChange={() => handleFilterChange('cpu', 'intel')}
                    />
                    <span className="ml-3 text-sm text-gray-700">Intel Xeon / Core</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.cpu.includes('amd')}
                      onChange={() => handleFilterChange('cpu', 'amd')}
                    />
                    <span className="ml-3 text-sm text-gray-700">AMD Ryzen / Threadripper</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.cpu.includes('apple')}
                      onChange={() => handleFilterChange('cpu', 'apple')}
                    />
                    <span className="ml-3 text-sm text-gray-700">Apple Silicon</span>
                  </label>
                </div>
              </div>

              {/* Graphics Cards */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Grafische Kaarten' : 'Graphics Cards'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.gpu.includes('nvidia')}
                      onChange={() => handleFilterChange('gpu', 'nvidia')}
                    />
                    <span className="ml-3 text-sm text-gray-700">NVIDIA RTX / Quadro</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.gpu.includes('amd')}
                      onChange={() => handleFilterChange('gpu', 'amd')}
                    />
                    <span className="ml-3 text-sm text-gray-700">AMD Radeon Pro</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.gpu.includes('intel')}
                      onChange={() => handleFilterChange('gpu', 'intel')}
                    />
                    <span className="ml-3 text-sm text-gray-700">Intel Arc Pro</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.gpu.includes('integrated')}
                      onChange={() => handleFilterChange('gpu', 'integrated')}
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      {isNL ? 'Geïntegreerde Grafische Kaart' : 'Integrated Graphics'}
                    </span>
                  </label>
                </div>
              </div>

              {/* RAM Capacity */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Geheugencapaciteit' : 'RAM Capacity'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.ram.includes('16gb-32gb')}
                      onChange={() => handleFilterChange('ram', '16gb-32gb')}
                    />
                    <span className="ml-3 text-sm text-gray-700">16GB - 32GB</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.ram.includes('64gb')}
                      onChange={() => handleFilterChange('ram', '64gb')}
                    />
                    <span className="ml-3 text-sm text-gray-700">64GB</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.ram.includes('128gb')}
                      onChange={() => handleFilterChange('ram', '128gb')}
                    />
                    <span className="ml-3 text-sm text-gray-700">128GB</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.ram.includes('256gb-plus')}
                      onChange={() => handleFilterChange('ram', '256gb-plus')}
                    />
                    <span className="ml-3 text-sm text-gray-700">256GB+</span>
                  </label>
                </div>
              </div>

              {/* Availability */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Beschikbaarheid' : 'Availability'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.availability.includes('in-stock')}
                      onChange={() => handleFilterChange('availability', 'in-stock')}
                    />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Op Voorraad' : 'In Stock'}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.availability.includes('pre-order')}
                      onChange={() => handleFilterChange('availability', 'pre-order')}
                    />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Voorbestelling' : 'Pre-Order'}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="filter-checkbox"
                      checked={filters.availability.includes('custom-config')}
                      onChange={() => handleFilterChange('availability', 'custom-config')}
                    />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Aangepaste Configuratie' : 'Custom Configuration'}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                <span className="text-gray-600">
                  {isNL ? (
                    <>{products.length} van 89 producten weergegeven</>
                  ) : (
                    <>
                      Showing <span>{products.length}</span> of 89 products
                    </>
                  )}
                </span>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Sorteren op:' : 'Sort by:'}</label>
                  <select
                    className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-primary pr-8"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="relevance">{isNL ? 'Relevantie' : 'Relevance'}</option>
                    <option value="name">{isNL ? 'Naam A-Z' : 'Name A-Z'}</option>
                    <option value="popularity">{isNL ? 'Populariteit' : 'Popularity'}</option>
                    <option value="newest">{isNL ? 'Nieuwste Eerst' : 'Newest First'}</option>
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Toon:' : 'Show:'}</label>
                  <select
                    className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-primary pr-8"
                    value={perPage}
                    onChange={(e) => setPerPage(parseInt(e.target.value))}
                  >
                    <option value="12">12</option>
                    <option value="24">24</option>
                    <option value="48">48</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Products */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {products.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow product-card"
                >
                  <div className="relative">
                    <div className="h-48 overflow-hidden bg-gray-50 flex items-center justify-center">
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${getAvailabilityBadge(product.availability)}`}>
                        {isNL ? product.availabilityNL : product.availability}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <input
                        type="checkbox"
                        className="compare-checkbox w-5 h-5 cursor-pointer"
                        checked={compareProducts.includes(product.id)}
                        onChange={() => handleCompareToggle(product.id)}
                      />
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${getCategoryColor(product.category)}`}>
                        {isNL ? product.categoryNL : product.category}
                      </span>
                      <div className="text-yellow-400 flex">{renderStars(product.rating)}</div>
                    </div>

                    <h3 className="text-lg font-semibold mb-1">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-3">{isNL ? product.descriptionNL : product.description}</p>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{isNL ? 'Processor:' : 'CPU:'}</span>
                        <span className="font-medium">{product.cpu}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{isNL ? 'Geheugen:' : 'RAM:'}</span>
                        <span className="font-medium">{product.ram}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{isNL ? 'Grafische Kaart:' : 'Graphics:'}</span>
                        <span className="font-medium">{product.graphics}</span>
                      </div>
                      {product.storage && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{isNL ? 'Opslag:' : 'Storage:'}</span>
                          <span className="font-medium">{product.storage}</span>
                        </div>
                      )}
                      {product.display && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{isNL ? 'Beeldscherm:' : 'Display:'}</span>
                          <span className="font-medium">{product.display}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-xs text-gray-500">
                          {isNL ? product.priceLabelNL : product.priceLabelEN}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="bg-gray-100 text-gray-600 px-3 py-2 rounded-lg whitespace-nowrap text-sm hover:bg-gray-200 cursor-pointer">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </div>
                        </button>
                        <a href={`${prefix}/contact`} className="bg-primary text-white px-3 py-2 rounded-lg whitespace-nowrap text-sm hover:bg-primary/90 cursor-pointer">
                          {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-12">
              <div className="text-gray-600">{isNL ? '1-24 van 89 resultaten weergegeven' : 'Showing 1-24 of 89 results'}</div>
              <div className="flex items-center space-x-2">
                <button
                  className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
                  disabled
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                </button>
                <button className="px-3 py-2 bg-primary text-white rounded cursor-pointer">1</button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">2</button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">3</button>
                <span className="px-2 text-gray-500">...</span>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">4</button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transform transition-transform duration-300 z-40 ${
          showComparisonBar ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <span className="font-medium">
                {isNL ? 'Werkstations Vergelijken' : 'Compare Products'} (<span>{compareProducts.length}</span>/4)
              </span>
              <div className="flex space-x-2">
                {compareProducts.map((productId) => {
                  const product = products.find((p) => p.id === productId);
                  return (
                    <div
                      key={productId}
                      className="bg-gray-100 px-3 py-1 rounded-full text-sm flex items-center space-x-2"
                    >
                      <span>{product?.name}</span>
                      <button
                        onClick={() => handleCompareToggle(productId)}
                        className="text-gray-500 hover:text-gray-700 cursor-pointer"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-close-line"></i>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="text-gray-600 hover:text-gray-800 cursor-pointer" onClick={clearComparison}>
                {isNL ? 'Alles Wissen' : 'Clear All'}
              </button>
              <button className="bg-primary text-white px-6 py-2 rounded-lg whitespace-nowrap font-medium hover:bg-primary/90 cursor-pointer">
                {isNL ? 'Selectie Vergelijken' : 'Compare Selected'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default WorkstationsPage;
